import {Checkbox, Column, FormControl, Table, Tooltip} from 'platform/components';
import {Box, Center, HStack, Icon, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {useMemo} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {useGetManufacturersQuery} from '@dms/api/core';
import {useGetHandlingUnitsQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getOptionsFromVatRates, useTenant, useVatRatesOptions} from '@dms/shared';
import {getOptionsFromHandlingUnits} from '@dms/shared-aftersales';

import {pushGtmEvent} from 'shared/web';

import {useMasterCheckbox} from '../hooks/useMasterCheckbox';
import {DeliveryNoteDocumentPreviewFormDefinition} from '../types/DeliveryNoteDocumentPreviewFormDefinition';
import {DeliveryNoteField} from '../types/DeliveryNoteField';
import {BulkActionsBanner} from './BulkActionsBanner';
import {PageTableRow} from './PageTableRow';

const ACTION_COLUMN_WIDTH = 10;
const SWITCH_COLUMN_WIDTH = 14;

interface ListOfItemsTableProps {
  isConfirmed: boolean;
  isLoadingSuggestions: boolean;
  isCollapsed: boolean;
  formApi: UseFormReturn<DeliveryNoteDocumentPreviewFormDefinition>;
  control: FormControl<DeliveryNoteDocumentPreviewFormDefinition>;
  currentPage: number;
  currentPageItems: DeliveryNoteField[];
}

export function ListOfItemsTable(props: ListOfItemsTableProps) {
  const {tenantCountry} = useTenant();

  const masterCheckbox = useMasterCheckbox({
    formApi: props.formApi,
    currentPageItems: props.currentPageItems,
  });

  const manufacturersQuery = useGetManufacturersQuery();

  const handlingUnitsQuery = useGetHandlingUnitsQuery();

  const vatRates = useVatRatesOptions();

  const tableColumns = useMemo(
    () =>
      getTableColumns({
        isConfirmed: props.isConfirmed,
        isAllSelected: masterCheckbox.isAllSelected,
        isIndeterminate: masterCheckbox.isIndeterminate,
        onToggleAll: masterCheckbox.onToggleAll,
      }),
    [
      props.isConfirmed,
      masterCheckbox.isAllSelected,
      masterCheckbox.isIndeterminate,
      masterCheckbox.onToggleAll,
    ]
  );

  const handlingUnitsOptions = useMemo(
    () => getOptionsFromHandlingUnits(handlingUnitsQuery.data),
    [handlingUnitsQuery.data]
  );

  const vatRatesOptions = useMemo(() => getOptionsFromVatRates(vatRates), [vatRates]);

  return (
    <Box overflow="auto" height="100%">
      <VStack>
        <BulkActionsBanner
          isConfirmed={props.isConfirmed}
          currentPage={props.currentPage}
          formApi={props.formApi}
          control={props.control}
        />
        <StyledTableWrapper $isCollapsed={props.isCollapsed}>
          <Table columns={tableColumns}>
            {props.currentPageItems.map((item) => (
              <PageTableRow
                key={item.field.id}
                item={item}
                isCollapsed={props.isCollapsed}
                isConfirmed={props.isConfirmed}
                isLoadingSuggestions={props.isLoadingSuggestions}
                formApi={props.formApi}
                control={props.control}
                countryCode={tenantCountry}
                manufacturers={manufacturersQuery.data}
                handlingUnits={handlingUnitsOptions}
                vatOptions={vatRatesOptions}
              />
            ))}
          </Table>
        </StyledTableWrapper>
      </VStack>
    </Box>
  );
}

// Disabled eslint because this component is super custom and we need to target specific css selectors
// eslint-disable-next-line eag/no-css-property
const StyledTableWrapper = styled.div<{$isCollapsed: boolean}>`
  height: 100%;
  overflow-x: auto;

  thead {
    position: sticky;
    top: 0;
    z-index: ${({theme}) => theme.zIndices.FULLSCREEN_MODAL};
    box-shadow: inset 0 -1px 0 ${({theme}) => theme.colors.general.separator};
  }

  tr th {
    background: ${({theme}) => theme.colors.palettes.neutral[10][100]};
  }

  tr td {
    background: ${({theme}) => theme.colors.general.white};
  }

  tbody tr:has(input[type='checkbox']:not([role='switch']):checked) > td {
    background: ${({theme}) => theme.colors.palettes.blue[10][100]};
  }

  tr > th:nth-last-child(-n + 2),
  tr > td:nth-last-child(-n + 2) {
    position: sticky;
  }

  tr > th:last-child,
  tr > td:last-child {
    right: 0;
    width: ${({theme}) => theme.getSize(ACTION_COLUMN_WIDTH)};
    min-width: ${({theme}) => theme.getSize(ACTION_COLUMN_WIDTH)};
    max-width: ${({theme}) => theme.getSize(ACTION_COLUMN_WIDTH)};
  }

  tr > th:nth-last-child(2),
  tr > td:nth-last-child(2) {
    right: ${({theme}) => theme.getSize(ACTION_COLUMN_WIDTH)};
    width: ${({theme}) => theme.getSize(SWITCH_COLUMN_WIDTH)};
    min-width: ${({theme}) => theme.getSize(SWITCH_COLUMN_WIDTH)};
    max-width: ${({theme}) => theme.getSize(SWITCH_COLUMN_WIDTH)};
    box-shadow: inset 1px 0 0 ${({theme}) => theme.colors.general.separator};
    border-left: 0;
  }
`;

type TableColumnsProps = {
  isConfirmed: boolean;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  onToggleAll: () => void;
};

function getTableColumns(props: TableColumnsProps): Column[] {
  return [
    {
      width: 12,
      element: (
        <Box backgroundColor="palettes.neutral.10.100">
          <Center>
            <Checkbox
              isDisabled={!props.isConfirmed}
              value={props.isAllSelected}
              isIndeterminate={props.isIndeterminate}
              onChange={() => {
                pushGtmEvent({event: 'button_click', event_id: 'ai_tool_dn_toggle_all_items'});
                props.onToggleAll();
              }}
            />
          </Center>
        </Box>
      ),
    },
    {
      width: 40,
      element: (
        <Box padding={2} backgroundColor="palettes.neutral.10.100">
          <Text size="small" align="left">
            {i18n.t('entity.warehouse.labels.number')}
          </Text>
        </Box>
      ),
    },
    {
      width: 40,
      element: (
        <Box padding={2} backgroundColor="palettes.neutral.10.100">
          <Text size="small" align="left">
            {i18n.t('entity.warehouse.labels.name')}
          </Text>
        </Box>
      ),
    },
    {
      width: 35,
      element: (
        <Box padding={2} backgroundColor="palettes.neutral.10.100">
          <Text size="small" align="left">
            {i18n.t('entity.warehouse.labels.manufacturer')}
          </Text>
        </Box>
      ),
    },
    {
      width: 18,
      element: (
        <Box padding={2} backgroundColor="palettes.neutral.10.100">
          <Text size="small" align="left">
            {i18n.t('entity.warehouse.labels.quantity')}
          </Text>
        </Box>
      ),
    },
    {
      width: 35,
      element: (
        <Box padding={2} backgroundColor="palettes.neutral.10.100">
          <HStack align="center" spacing={1}>
            <Text size="small" align="left">
              {i18n.t('entity.warehouse.labels.unitPrice')}
            </Text>
            <Tooltip
              label={i18n.t('entity.documentExtraction.labels.unitPriceColumnTooltip')}
              placement="top"
            >
              <Icon value="action/help" size={4} color="text.secondary" />
            </Tooltip>
          </HStack>
        </Box>
      ),
    },
    {
      width: 35,
      element: (
        <Box padding={2} backgroundColor="palettes.neutral.10.100">
          <Text size="small" align="left">
            {i18n.t('entity.warehouse.labels.totalPrice')}
          </Text>
        </Box>
      ),
    },
    {
      width: 20,
      element: (
        <Box padding={2} backgroundColor="palettes.neutral.10.100">
          <Text size="small" align="left">
            {i18n.t('entity.warehouse.labels.unit')}
          </Text>
        </Box>
      ),
    },
    {
      width: 23,
      element: (
        <Box padding={2} backgroundColor="palettes.neutral.10.100">
          <Text size="small" align="left">
            {i18n.t('entity.warehouse.labels.vat')}
          </Text>
        </Box>
      ),
    },
    {
      width: SWITCH_COLUMN_WIDTH,
      element: <Box backgroundColor="palettes.neutral.10.100" />,
    },
    {
      width: ACTION_COLUMN_WIDTH,
      element: <Box backgroundColor="palettes.neutral.10.100" />,
    },
  ];
}
